import type { RestoreFormRequest } from 'apiConnector/batch-workspace';
import { batchWorkspaceApiConnector } from 'apiConnector/batch-workspace';
import { lastQuoteApi } from 'apiConnector/quote/lastquote';
import type { BatchPersistedTabContent } from 'apiConnector/tabs/tabs.model';
import { BaseQueueEpicFactory } from 'epic/utils/queuing/baseQueueEpicFactory';
import type { BatchFormResult } from 'models/forms/BatchFormResult';
import { Action } from 'redux';
import { ofType } from 'redux-observable';
import type { Observable } from 'rxjs';
import { forkJoin, merge, of } from 'rxjs';
import { catchError, filter, ignoreElements, map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  batchFormsRestoreRequested,
  batchPreviousQuoteLoaded,
  defaultBatchFormFailed,
} from 'state/batchForm/batchForm.action';
import * as actionTypes from 'state/batchForm/batchForm.actionTypes';
import { defaultBatchFormReceivedThunk } from 'state/batchForm/batchForm.thunks';
import { QuoteWithRegulation } from 'state/quotes/results/result.model';
import type { EpicWithThunk, Thunk } from 'state/state.common';
import { isExternalQuoteId } from 'utils/string';

export interface BatchFormRestoreApi {
  restoreForm(request: RestoreFormRequest): Observable<BatchFormResult>;
}

export interface BatchRestoreFormQueueItem {
  tabId: string;
  negotiationId: string;
  quoteId?: string;
  pricerRfqId?: string;
}

const BATCH_RESTORE_FORM_MAX_QUEUE_SIZE = 5;

export class BatchRestoreFormQueueEpicFactory extends BaseQueueEpicFactory<BatchRestoreFormQueueItem> {
  public restoreFormEpic(): EpicWithThunk {
    return (actions$, state$, { httpClient }) =>
      actions$.pipe(
        ofType('USER_CURRENT_RECEIVED'),
        take(1),
        withLatestFrom(state$),
        mergeMap(([_]) => {
          return this.itemsToHandle$.pipe(
            mergeMap(itemsToHandle => {
              this.startHandling(...itemsToHandle);

              return merge(
                of(...itemsToHandle).pipe(
                  mergeMap(item => {
                    const restoreFormRequest: RestoreFormRequest = {
                      negotiationId: item.negotiationId,
                      pricerRfqId: item.pricerRfqId,
                      quoteId: item.quoteId,
                    };

                    const restoreForm$ = batchWorkspaceApiConnector(httpClient).restoreForm(restoreFormRequest);

                    return restoreForm$.pipe(
                      mergeMap(response => {
                        const restoredQuoteId = getInternalQuoteId(response.quoteId, item.quoteId);
                        const getLastQuote$ = restoredQuoteId
                          ? lastQuoteApi(httpClient)
                              .getLastQuote(restoredQuoteId)
                              .pipe(catchError(_ => of(null)))
                          : of(null);

                        return forkJoin([of(response), getLastQuote$, of(restoredQuoteId)]);
                      }),
                      mergeMap(([response, quoteWithRegulation, restoredQuoteId]) => {
                        this.completeHandling(item);
                        const actions = buildActions(item, response, quoteWithRegulation, restoredQuoteId);
                        return of(...actions);
                      }),
                      catchError((_: Error) => {
                        this.completeHandling(item);
                        return of(defaultBatchFormFailed(item.negotiationId));
                      }),
                    );
                  }),
                ),
              );
            }),
          );
        }),
      );
  }

  public addItemToQueueOnRollbackEpic(): EpicWithThunk {
    return actions$ =>
      actions$.pipe(
        ofType(actionTypes.BATCH_FORM_ROLLBACK_REQUESTED),
        tap(action => this.addToQueue({ negotiationId: action.formId, tabId: action.tabId })),
        ignoreElements(),
      );
  }

  public addLoadingFormsOnTabContentRestoredEpic(): EpicWithThunk {
    return (actions$, state$) =>
      actions$.pipe(
        ofType('WORKSPACE_TAB_CONTENT_FETCHED'),
        filter(action => action.workspace === 'newBatch'),
        withLatestFrom(state$),
        filter(([action]) => {
          return (action.tabContent as BatchPersistedTabContent).forms?.length > 0;
        }),
        map(([action]) => {
          const tabContent: BatchPersistedTabContent = action.tabContent as BatchPersistedTabContent;
          const negotiationIds = tabContent.negotiationIds;
          const forms = tabContent.forms;
          const productType = tabContent.productType ?? '';
          const productSubType = tabContent.productSubtype ?? '';
          const wrapper = tabContent.wrapper ?? '';
          const queueItem: BatchRestoreFormQueueItem[] = forms.map(form => ({
            tabId: action.tabId,
            negotiationId: form.negotiationId,
            quoteId: form.quoteId,
            pricerRfqId: form.pricerRfqId,
            productType,
            productSubType,
            wrapper,
          }));
          this.addToQueue(...queueItem);
          return batchFormsRestoreRequested(action.tabId, negotiationIds, forms, productType, productSubType, wrapper);
        }),
      );
  }
}
export const batchRestoreFormQueueEpics = new BatchRestoreFormQueueEpicFactory(
  'batchRestoreFormQueueEpics',
  BATCH_RESTORE_FORM_MAX_QUEUE_SIZE,
);

function buildActions(
  action: BatchRestoreFormQueueItem,
  form: BatchFormResult,
  quoteWithRegulation: QuoteWithRegulation | null,
  restoredQuoteId?: string,
) {
  const actions: (Action | Thunk<void>)[] = [
    defaultBatchFormReceivedThunk(
      action.tabId,
      action.negotiationId,
      form.negotiationId,
      form.productView,
      form.actions,
      form.values,
      form.fieldsMetadata,
      form.quoteQuery,
      form.version,
      form.mustForceAdvancedMode ?? false,
      form.allowedRequests,
      form.errors,
    ),
  ];

  if (quoteWithRegulation && restoredQuoteId) {
    actions.push(batchPreviousQuoteLoaded(form.negotiationId, restoredQuoteId, quoteWithRegulation.quote));
  }

  return actions;
}

function getInternalQuoteId(restoredQuoteId?: string, persistedQuoteId?: string): string | undefined {
  if (restoredQuoteId && !isExternalQuoteId(restoredQuoteId)) {
    return restoredQuoteId;
  }

  if (persistedQuoteId && !isExternalQuoteId(persistedQuoteId)) {
    return persistedQuoteId;
  }

  return undefined;
}
